import React, { useState, useEffect } from 'react';
import Sidebar from './../components/sidebar';
import { Container, Row, Col, Table, Button, ButtonGroup, Spinner, Alert, Modal, Form, Card } from 'react-bootstrap';
import axios from 'axios';

const Encuesta = () => {
  const [view, setView] = useState('campañas'); // 'campañas' | 'encuesta'
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [encuesta, setEncuesta] = useState({
    id: null,
    idusuario: null,
    campana: null,
    pregunta: '',
    respuesta_1: '',
    evento_1: '',
    respuesta_2: '',
    evento_2: '',
    respuesta_3: '',
    evento_3: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({
    type: '',       // 'pregunta' | 'respuesta' | 'evento'
    index: null,    // solo para respuestas/eventos
    content: '',
    isNew: false
  });
  
  const [feedback, setFeedback] = useState({
    show: false,
    message: '',
    variant: 'success'
  });

  const [userId, setUserId] = useState(null);

  // Obtener usuario logueado
  useEffect(() => {
    const loginUserJSON = window.localStorage.getItem("loginUser");
    if (loginUserJSON) {
      const user = JSON.parse(loginUserJSON);
      setUserId(user.id);
    }
  }, []);

  // Función para obtener las campañas del usuario
  const fetchCampaigns = async () => {
    if (!userId) return;
    
    try {
      setCampaignsLoading(true);
      const response = await axios.get(`https://api.mrcirp.com/public/userpaneltodos`);
      
      // Filtrar solo las campañas del usuario actual
      const userCampaigns = response.data.filter(campaign => campaign.id_creador == userId);
      setCampaigns(userCampaigns);
      setCampaignsLoading(false);
    } catch (err) {
      setError(err.message || 'Error al cargar las campañas');
      setCampaignsLoading(false);
    }
  };

  // Función para mapear los datos de la API al estado local
  const mapApiDataToState = (apiData) => {
    return {
      id: apiData.id || null,
      idusuario: apiData.idusuario || userId,
      campana: apiData.campana || selectedCampaign,
      pregunta: apiData.pregunta || '',
      respuesta_1: apiData.respuesta_1 || '',
      evento_1: apiData.evento_1 || '',
      respuesta_2: apiData.respuesta_2 || '',
      evento_2: apiData.evento_2 || '',
      respuesta_3: apiData.respuesta_3 || '',
      evento_3: apiData.evento_3 || ''
    };
  };

  // Función para obtener encuesta de la campaña seleccionada
  const fetchEncuesta = async () => {
    if (!userId || !selectedCampaign) return;
    
    try {
      setLoading(true);
      // Primero buscamos todas las encuestas del usuario
      const response = await axios.get(`https://api.mrcirp.com/public/encuesta`, {
        params: {
          idusuario: userId
        }
      });

      // Buscamos si existe una encuesta para esta campaña específica
      let encuestaData = null;
      if (response.data && response.data.length > 0) {
        encuestaData = response.data.find(
          item => item.campana === selectedCampaign
        );
      }

      if (encuestaData) {
        setEncuesta(mapApiDataToState(encuestaData));
      } else {
        // No hay encuesta para esta campaña - crear estructura vacía
        setEncuesta({
          id: null,
          idusuario: userId,
          campana: selectedCampaign,
          pregunta: '',
          respuesta_1: '',
          evento_1: '',
          respuesta_2: '',
          evento_2: '',
          respuesta_3: '',
          evento_3: ''
        });
      }
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 404) {
        // No existe encuesta para este usuario/campaña
        setEncuesta({
          id: null,
          idusuario: userId,
          campana: selectedCampaign,
          pregunta: '',
          respuesta_1: '',
          evento_1: '',
          respuesta_2: '',
          evento_2: '',
          respuesta_3: '',
          evento_3: ''
        });
      } else {
        setError(err.message || 'Error al cargar la encuesta');
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchCampaigns();
    }
  }, [userId]);

  useEffect(() => {
    if (selectedCampaign) {
      fetchEncuesta();
    }
  }, [selectedCampaign]);

  // Manejo del modal
  const handleShowModal = (type, index = null) => {
    let content = '';
    
    if (type === 'pregunta') {
      content = encuesta.pregunta;
    } else if (type === 'respuesta') {
      content = encuesta[`respuesta_${index + 1}`];
    } else if (type === 'evento') {
      content = encuesta[`evento_${index + 1}`] || 'no'; // Valor por defecto 'no'
    }
    
    setModalData({
      type,
      index,
      content,
      isNew: !content
    });
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  // Función para guardar cambios
  const handleSave = async () => {
    if (modalData.type !== 'evento' && !modalData.content.trim()) {
      setFeedback({
        show: true,
        message: 'El contenido no puede estar vacío',
        variant: 'warning'
      });
      return;
    }

    try {
      // Actualizar el estado local primero
      const updatedEncuesta = { ...encuesta };
      
      if (modalData.type === 'pregunta') {
        updatedEncuesta.pregunta = modalData.content;
      } else if (modalData.type === 'respuesta') {
        updatedEncuesta[`respuesta_${modalData.index + 1}`] = modalData.content;
      } else if (modalData.type === 'evento') {
        updatedEncuesta[`evento_${modalData.index + 1}`] = modalData.content;
      }
      
      setEncuesta(updatedEncuesta);
      
      // Determinar si es creación o actualización
      const isUpdate = encuesta.id !== null;
      const method = isUpdate ? 'put' : 'post';
      const url = `https://api.mrcirp.com/public/encuesta${isUpdate ? `/${encuesta.id}` : ''}`;

      const response = await axios[method](url, updatedEncuesta);
      
      // Si es creación, actualizar el ID de la encuesta
      if (!isUpdate && response.data.id) {
        updatedEncuesta.id = response.data.id;
        setEncuesta(updatedEncuesta);
      }
      
      setFeedback({
        show: true,
        message: isUpdate ? 'Actualizado correctamente' : 'Creado correctamente',
        variant: 'success'
      });
      
      handleCloseModal();
    } catch (error) {
      console.error('Error al guardar:', error);
      setFeedback({
        show: true,
        message: `Error al ${modalData.isNew ? 'crear' : 'actualizar'}`,
        variant: 'danger'
      });
    }
  };

  // Función para seleccionar una campaña
  const selectCampaign = (campaignId) => {
    setSelectedCampaign(campaignId);
    setView('encuesta');
  };

  // Función para volver a la vista de campañas
  const goBackToCampaigns = () => {
    setView('campañas');
    setSelectedCampaign(null);
  };

  if (campaignsLoading && view === 'campañas') {
    return (
      <Sidebar>
        <Container className="mt-4 mb-5">
          <Row className="justify-content-center">
            <Col md="auto">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Cargando campañas...</p>
            </Col>
          </Row>
        </Container>
      </Sidebar>
    );
  }

  if (loading && view === 'encuesta') {
    return (
      <Sidebar>
        <Container className="mt-4 mb-5">
          <Row className="justify-content-center">
            <Col md="auto">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Cargando encuesta...</p>
            </Col>
          </Row>
        </Container>
      </Sidebar>
    );
  }

  if (error) {
    return (
      <Sidebar>
        <Container className="mt-4 mb-5">
          <Row>
            <Col>
              <Alert variant="danger">
                Error: {error}
              </Alert>
            </Col>
          </Row>
        </Container>
      </Sidebar>
    );
  }

  // Vista de selección de campañas
  if (view === 'campañas') {
    return (
      <Sidebar>
        <Container className="mt-4 mb-5">
          <Row className="mb-4">
            <Col>
              <h3>Mis Campañas</h3>
              <p>Selecciona una campaña para configurar su encuesta</p>
            </Col>
          </Row>

          {campaigns.length === 0 ? (
            <Alert variant="info">
              No tienes campañas creadas. Primero crea una campaña para configurar su encuesta.
            </Alert>
          ) : (
            <Row>
              {campaigns.map(campaign => (
                <Col md={4} key={campaign.id} className="mb-4">
                  <Card 
                    className="h-100 cursor-pointer"
                    onClick={() => selectCampaign(campaign.usuario)}
                  >
                    <Card.Body>
                      <Card.Title>{campaign.usuario}</Card.Title>
                      <Card.Text>
                        <small className="text-muted">
                          Creada: {new Date(campaign.fecha).toLocaleDateString()}
                        </small>
                      </Card.Text>
                    </Card.Body>
                    <Card.Footer>
                      <Button 
                        variant="primary" 
                        block
                        onClick={() => selectCampaign(campaign.usuario)}
                      >
                        Configurar Encuesta
                      </Button>
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </Sidebar>
    );
  }

  // Vista de encuesta
  return (
    <Sidebar>
      <Container className="mt-4 mb-5">
        {/* Botón para volver a campañas */}
        <Button 
          variant="outline-secondary" 
          onClick={goBackToCampaigns}
          className="mb-3"
        >
          &larr; Volver a Mis Campañas
        </Button>

        {/* Feedback de operaciones */}
        {feedback.show && (
          <Alert 
            variant={feedback.variant} 
            onClose={() => setFeedback({...feedback, show: false})} 
            dismissible
            className="mt-3"
          >
            {feedback.message}
          </Alert>
        )}

        <Row className="mb-4">
          <Col>
            <h3>Encuesta de la Campaña: {selectedCampaign}</h3>
            {encuesta.id === null && (
              <p className="text-muted">No hay encuesta configurada para esta campaña</p>
            )}
          </Col>
        </Row>

        <Table striped bordered hover className="mb-4">
          <thead>
            <tr>
              <th>Elemento</th>
              <th>Contenido</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {/* Fila de Pregunta */}
            <tr>
              <td><strong>Pregunta</strong></td>
              <td>{encuesta.pregunta || <span className="text-muted">No definida</span>}</td>
              <td>
                <Button 
                  variant={encuesta.pregunta ? "primary" : "success"}
                  size="sm"
                  onClick={() => handleShowModal('pregunta')}
                >
                  {encuesta.pregunta ? "Editar" : "Añadir"}
                </Button>
              </td>
            </tr>
            
            {/* Filas de Respuestas y Eventos */}
            {[1, 2, 3].map((index) => (
              <React.Fragment key={`respuesta-${index}`}>
                <tr>
                  <td><strong>Respuesta {index}</strong></td>
                  <td>{encuesta[`respuesta_${index}`] || <span className="text-muted">No definida</span>}</td>
                  <td>
                    <ButtonGroup>
                      <Button 
                        variant={encuesta[`respuesta_${index}`] ? "warning" : "success"}
                        size="sm"
                        onClick={() => handleShowModal('respuesta', index - 1)}
                      >
                        {encuesta[`respuesta_${index}`] ? "Editar" : "Añadir"}
                      </Button>
                      <Button 
                        variant="info" 
                        size="sm"
                        onClick={() => handleShowModal('evento', index - 1)}
                        disabled={!encuesta[`respuesta_${index}`]}
                      >
                        Evento
                      </Button>
                    </ButtonGroup>
                  </td>
                </tr>
                {encuesta[`respuesta_${index}`] && (
                  <tr>
                    <td><strong>Evento {index}</strong></td>
                    <td>
                      {encuesta[`evento_${index}`] === 'cliente potencial' ? 'Cliente Potencial' : 
                       encuesta[`evento_${index}`] === 'no' ? 'No' : 
                       <span className="text-muted">No definido</span>}
                    </td>
                    <td>
                      <Button 
                        variant={encuesta[`evento_${index}`] ? "info" : "success"}
                        size="sm"
                        onClick={() => handleShowModal('evento', index - 1)}
                      >
                        {encuesta[`evento_${index}`] ? "Editar" : "Añadir"}
                      </Button>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>

        {/* Modal para edición */}
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {modalData.isNew ? 'Añadir' : 'Editar'} {
                modalData.type === 'pregunta' ? 'Pregunta' : 
                modalData.type === 'respuesta' ? `Respuesta ${modalData.index + 1}` : 
                `Evento ${modalData.index + 1}`
              }
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalData.type === 'evento' ? (
              <Form.Group>
                <Form.Label>Tipo de Evento:</Form.Label>
                <Form.Control
                  as="select"
                  value={modalData.content}
                  onChange={(e) => setModalData({...modalData, content: e.target.value})}
                >
                  <option value="no">No</option>
                  <option value="cliente potencial">Cliente Potencial</option>
                </Form.Control>
              </Form.Group>
            ) : (
              <Form.Group>
                <Form.Label>
                  {modalData.type === 'pregunta' ? 'Texto de la pregunta:' : 
                   `Texto de la respuesta ${modalData.index + 1}:`}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={modalData.content}
                  onChange={(e) => setModalData({...modalData, content: e.target.value})}
                  placeholder={`Escribe aquí ${
                    modalData.type === 'pregunta' ? 'la pregunta' : 'la respuesta'
                  }`}
                  autoFocus
                />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSave}>
              {modalData.isNew ? 'Guardar' : 'Actualizar'}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Sidebar>
  );
};

export default Encuesta;